import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Group, List, Stack, Table, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconClock, IconFileImport, IconPhone } from '@tabler/icons-react';
import Link from 'next/link';
import { ImportDraft, ImportDraftCommitResult, ImportRecipe, TRANSFORM_LABELS } from '@/services/import-draft/import-draft-model';
import {
  columnLabel, fieldSummaryRows, heldBackCount, ignoredColumnsWithData, ignoredReason, importFailureSummary, importTypeName,
  importOutcomeState, isCommitted, isExtracted, isSavedRecipe, leadingZeroCounts, queueableCount, softIssueCount,
} from '@/services/import-draft/import-draft-helpers';
import ImportDraftService, { isApiError } from '@/services/import-draft/import-draft-service';
import ProcessNowButton from './ProcessNowButton';
import { CategoryMoveNotice, MixedContactsNotice, SavedRecipeNotice } from './RecipeNotices';

interface Props {
  draft: ImportDraft;
  recipe: ImportRecipe | null;
  result: ImportDraftCommitResult | null;
  committing: boolean;
  error?: string | null;
  onBack: () => void;
  onEditMapping?: () => void;
  onRedetect?: () => void;
  onUndoCategoryMove?: (columnIndex: number) => void;
  onCommit: () => void;
  onStartAnother: () => void;
}

const FAST_POLL_MS = 5000;
const FAST_POLLS = 24;
const SLOW_EVERY = 6;
const MAX_POLLS = 360;

export default function ConfirmStep(props: Props) {
  if (props.result || isCommitted(props.draft)) return <DoneState {...props} />;
  return <ReadyState {...props} />;
}

function ReadyState({ draft, recipe, committing, error, onBack, onEditMapping, onRedetect, onUndoCategoryMove, onCommit }: Props) {
  const summary = draft.Summary;
  const kind = importTypeName(draft.ImportType).toLowerCase();
  const profiles = draft.Profiles || [];
  const activeRecipe = recipe || draft.Recipe || null;
  const rows = fieldSummaryRows(activeRecipe, profiles);
  const ignoredWithData = isExtracted(draft) ? [] : ignoredColumnsWithData(activeRecipe, profiles);
  const queueable = queueableCount(summary);
  const heldBack = heldBackCount(summary);
  const softIssues = softIssueCount(summary);
  const zeros = leadingZeroCounts(summary);
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Stack gap="md" data-testid="confirm-step">
      <div>
        <Title order={4}>Ready to import</Title>
        <Text size="sm" c="dimmed">Nothing has been saved yet. Check which column feeds each field, then start the import.</Text>
      </div>

      {isSavedRecipe(draft) && <SavedRecipeNotice draft={draft} busy={committing} onRedetect={onRedetect} />}
      <MixedContactsNotice draft={draft} />
      <CategoryMoveNotice draft={draft} recipe={activeRecipe} busy={committing} onUndo={onUndoCategoryMove} />

      {summary && (
        <List spacing={4} size="sm">
          <List.Item><b>{queueable.toLocaleString()}</b> {kind} records from <b>{draft.SourceFileName}</b> will be imported</List.Item>
          {heldBack > 0 && <List.Item>{heldBack.toLocaleString()} records will not be imported because they would be rejected</List.Item>}
          {softIssues > 0 && <List.Item>{softIssues.toLocaleString()} records have warnings that can be corrected after the import</List.Item>}
          <List.Item>Existing {kind} records with a matching code or name are updated. No records are deleted.</List.Item>
          <List.Item>The mapping is saved and reused for files with the same layout.</List.Item>
        </List>
      )}

      {(zeros.restored > 0 || zeros.uncertain > 0) && (
        <Alert color={zeros.uncertain > 0 ? 'yellow' : 'blue'} icon={<IconPhone size={16} />} title="Phone numbers stored as numbers in Excel" data-testid="leading-zero-notice">
          {zeros.restored > 0 && (
            <Text size="sm">{zeros.restored.toLocaleString()} phone number{zeros.restored === 1 ? ' was' : 's were'} missing the leading zero. The zero has been put back.</Text>
          )}
          {zeros.uncertain > 0 && (
            <Text size="sm">{zeros.uncertain.toLocaleString()} phone number{zeros.uncertain === 1 ? '' : 's'} may have lost a leading zero and could not be corrected. Check these after the import.</Text>
          )}
        </Alert>
      )}

      {rows.length > 0 && (
        <div data-testid="confirm-field-table">
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>{rows.length} field{rows.length === 1 ? '' : 's'} will be filled from the file</Text>
            {onEditMapping && <Button size="compact-xs" variant="subtle" onClick={onEditMapping} disabled={committing}>Change the mapping</Button>}
          </Group>
          <Table verticalSpacing={4} fz="sm" withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ServCraft field</Table.Th>
                <Table.Th>Column in file</Table.Th>
                <Table.Th>Example</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(row => (
                <Table.Tr key={row.target} data-testid={`confirm-field-${row.target}`}>
                  <Table.Td>{row.target}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.source}</Text>
                    {row.transform !== 'Copy' && row.transform !== 'Constant' && (
                      <Text size="xs" c="dimmed">{TRANSFORM_LABELS[row.transform]}{row.argument ? ` (${row.argument})` : ''}</Text>
                    )}
                  </Table.Td>
                  <Table.Td><Text size="sm" c={row.sample ? undefined : 'dimmed'} lineClamp={1} title={row.sample || ''}>{row.sample || '-'}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {ignoredWithData.length > 0 && (
        <div data-testid="confirm-ignored-columns">
          <Text size="sm" fw={500}>Columns with data that will not be imported</Text>
          <List size="sm" spacing={2}>
            {ignoredWithData.map(p => (
              <List.Item key={p.Index}>
                <b>{columnLabel(profiles, p.Index)}</b>{p.Samples[0] ? ` (e.g. ${p.Samples[0]})` : ''}{ignoredReason(activeRecipe, p.Index) ? `: ${ignoredReason(activeRecipe, p.Index)}` : ''}
              </List.Item>
            ))}
          </List>
        </div>
      )}

      {heldBack > 0 && (
        <Checkbox
          checked={acknowledged}
          onChange={e => setAcknowledged(e.currentTarget.checked)}
          label={`I understand that ${heldBack.toLocaleString()} records have errors and will not be imported. Import the rest anyway.`}
        />
      )}

      {error && <Alert color="red">{error}</Alert>}

      <Group>
        <Button variant="default" onClick={onBack} disabled={committing}>Check the records</Button>
        <Button onClick={onCommit} loading={committing} disabled={committing || queueable === 0 || (heldBack > 0 && !acknowledged)} leftSection={<IconFileImport size={16} />}>
          Import {queueable.toLocaleString()} records
        </Button>
      </Group>
    </Stack>
  );
}

function DoneState({ draft, result, onStartAnother }: Props) {
  const [latest, setLatest] = useState<ImportDraft>(draft);
  const importID = result?.ImportID || draft.ImportID || null;
  const summary = latest.Summary || draft.Summary;
  const queued = result?.RowCount ?? (summary ? queueableCount(summary) : draft.RowCount);
  const leftOut = heldBackCount(summary);
  const failure = importFailureSummary(latest);
  const outcome = importOutcomeState(latest.Summary);
  const finished = outcome !== 'queued';

  useEffect(() => { setLatest(draft); }, [draft]);

  useEffect(() => {
    if (finished) return undefined;
    let stopped = false;
    let ticks = 0;
    const timer = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_POLLS) {
        clearInterval(timer);
        return;
      }
      if (ticks > FAST_POLLS && ticks % SLOW_EVERY !== 0) return;
      const loaded = await ImportDraftService.get(draft.ID);
      if (stopped || isApiError(loaded)) return;
      setLatest(loaded as ImportDraft);
    }, FAST_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [draft.ID, finished]);

  const counts = `${queued ? queued.toLocaleString() : 'The'} records from ${draft.SourceFileName}`;
  const leftOutText = leftOut > 0 ? ` ${leftOut.toLocaleString()} records with errors were left out.` : '';

  return (
    <Stack gap="md" data-testid="confirm-step-done">
      {outcome === 'queued' && (
        <Alert color="blue" icon={<IconClock size={16} />} title="Import created" data-testid="import-queued">
          <Text size="sm">{counts} have been queued.{leftOutText} The import is queued and will run shortly. Its progress is shown on the Imports page.</Text>
        </Alert>
      )}
      {outcome === 'completed' && (
        <Alert color="green" icon={<IconCheck size={16} />} title="Imported" data-testid="import-completed">
          <Text size="sm">{counts} have been imported.{leftOutText}</Text>
        </Alert>
      )}
      {outcome === 'errors' && (
        <Alert color="yellow" icon={<IconCheck size={16} />} title="Import finished" data-testid="import-finished-with-errors">
          <Text size="sm">The import of {counts.charAt(0).toLowerCase() + counts.slice(1)} has finished, but some records were rejected.{leftOutText}</Text>
        </Alert>
      )}
      {(outcome === 'errors' || !!failure) && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Some queued records failed during import" data-testid="import-failures">
          <Text size="sm">{failure || 'The import finished with errors. Download the error report from the Imports page to see which records were rejected.'}</Text>
        </Alert>
      )}
      <Group>
        <Button component={Link} href="/settings/import/list" leftSection={<IconFileImport size={16} />}>View imports</Button>
        <Button variant="default" onClick={onStartAnother}>Import another file</Button>
      </Group>
      {result && importID && !finished && <ProcessNowButton importID={importID} />}
    </Stack>
  );
}
